import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCurrentUser } from '../../context/CurrentUserContext';
import { refusalRedirectFor } from '../../utils/routeAccess';
import { useRefusalToast } from './useRefusalToast';

interface ContentOrAdminRouteProps {
  children: React.ReactNode;
}

/**
 * ContentOrAdminRoute component protects routes that require content or admin role
 * Extends ProtectedRoute logic by checking for either content or admin role
 * Redirects unauthorized users to refusalRedirectFor(roles) (/dashboard; a sales-only user to /crm/partner-enquiries) with an error message
 */
const ContentOrAdminRoute: React.FC<ContentOrAdminRouteProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { roles, isAdmin, isContent, loading: rolesLoading } = useCurrentUser();
  const location = useLocation();
  const isAuthorized = isAdmin || isContent;

  // The permission toast fires from an effect, once per refusal (see useRefusalToast)
  useRefusalToast(
    'You do not have permission to access this page. Content or Admin role required.',
    !authLoading && !rolesLoading && !!user && !isAuthorized
  );

  // Show loading spinner while checking authentication and roles
  if (authLoading || rolesLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" state={{ returnUrl: location.pathname }} replace />;
  }

  // If authenticated but doesn't have content or admin role, redirect (dashboard; a sales-only user to Partner Enquiries) with error message
  if (!isAuthorized) {
    return <Navigate to={refusalRedirectFor(roles)} replace />;
  }

  // User is authenticated and has content or admin role, render children
  return <>{children}</>;
};

export default ContentOrAdminRoute;
